import React, { useState, useRef, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocationCascade } from "@/components/LocationCascade";
import { Sparkles, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const ProductForm = ({ open, setOpen, product, onSaved }) => {
  const { t, lang } = useLang();
  const fileRef = useRef();
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cats, setCats] = useState([]);
  const blank = { name: "", description: "", price: "", category: "", subcategory: "", stock: 10, image: "", region: "", prefecture: "", commune: "", canton: "", localite: "" };
  const [form, setForm] = useState(() => product || blank);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, [open]);

  React.useEffect(() => {
    const base = product || blank;
    setForm({ ...blank, ...base, category: base.category || (cats[0]?.key || "") });
  }, [product, open, cats.length]); // eslint-disable-line

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const genDesc = async () => {
    if (!form.name) { toast.error(t("product_name")); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/ai/generate-description", { name: form.name, category: form.category, keywords: form.description, lang });
      upd("description", data.description);
      toast.success("AI ✨");
    } catch (e) { toast.error(apiError(e)); } finally { setGenLoading(false); }
  };

  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("image", data.url);
      toast.success(t("upload_image"));
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock), images: form.image ? [form.image] : [] };
      if (product?.id) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success(t("save"));
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-form">
        <DialogHeader><DialogTitle className="font-display">{product?.id ? t("edit") : t("add_product")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="text-xs">{t("product_name")}</Label><Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="pf-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">{t("category")}</Label>
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c[lang] || c.fr}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{t("price")} (FCFA)</Label><Input type="number" value={form.price} onChange={(e) => upd("price", e.target.value)} required data-testid="pf-price" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">{t("subcategory")}</Label><Input value={form.subcategory} onChange={(e) => upd("subcategory", e.target.value)} placeholder={t("subcategory_ph")} data-testid="pf-subcategory" /></div>
            <div><Label className="text-xs">{t("stock")}</Label><Input type="number" value={form.stock} onChange={(e) => upd("stock", e.target.value)} data-testid="pf-stock" /></div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">{t("location")}</Label>
            <LocationCascade value={form} onChange={(loc) => setForm((f) => ({ ...f, ...loc }))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">{t("description")}</Label>
              <button type="button" onClick={genDesc} disabled={genLoading} className="text-xs flex items-center gap-1 text-mango-600 font-semibold hover:text-mango-700" data-testid="pf-generate-ai">
                {genLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {genLoading ? t("generating") : t("generate_ai")}
              </button>
            </div>
            <Textarea rows={4} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="pf-description" />
          </div>
          <div>
            <Label className="text-xs">{t("image")}</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.image && <img src={form.image.startsWith("http") ? form.image : `${process.env.REACT_APP_BACKEND_URL}${form.image}`} alt="" className="w-16 h-16 rounded-lg object-cover border" />}
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadImg} className="hidden" data-testid="pf-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pf-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}{t("upload_image")}
              </Button>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">{t("cancel")}</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-emerald-800 hover:bg-emerald-900" data-testid="pf-submit">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
